import { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import { type ArbeidsarkIconKeys, SVGIcon } from "~/components"
import { ZIndexRange } from "~/types/style"
import { useClickOutside } from "~/ur-react-hooks"
import { useWindowSize } from "~/utils/hooks"

const MenuDropdownWrapper = styled.div`
  position: relative;
  width: 32px;
  height: 44px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  display: flex;
  :hover {
    background-color: ${(props) => props.theme.colors.primary95};
    cursor: pointer;
  }

  ${(props) => props.theme.media.pad} {
    width: 45px;
    height: 45px;
    border-radius: 999px;
  }
`
interface MenuDropdownItemsWrapperProps {
  show: boolean
  growUp: boolean
  offset?: {
    x?: number
    y?: number
  }
}
const MenuDropdownItemsWrapper = styled.div<MenuDropdownItemsWrapperProps>`
  background: white;
  border-radius: ${(props) => props.theme.sizes.defaultBorderRadius};
  box-shadow: ${(props) => props.theme.shadow.default};

  position: absolute;
  display: ${(props) => (props.show ? "flex" : "none")};
  flex-direction: column;
  transform: ${(props) =>
    props.growUp
      ? `translate(
          -${props.offset?.x ? props.offset.x : 40}%, 
          -${props.offset?.y ? props.offset.y : 64}%
        )`
      : `translate(
          -${props.offset?.x ? props.offset.x : 40}%, 
          ${props.offset?.y ? props.offset.y : 64}%
        )`};

  z-index: ${ZIndexRange.Dropdowns};

  ${(props) => props.theme.media.pad} {
    transform: ${(props) =>
      props.growUp
        ? `translate(-${props.offset?.x ? props.offset.x : 40}%, -${
            props.offset?.y ? props.offset.y : 61
          }%)`
        : `translate(-${props.offset?.x ? props.offset.x : 40}%, ${
            props.offset?.y ? props.offset.y : 61
          }%)`};
  }
  div:first-child {
    border-top-left-radius: ${(props) => props.theme.sizes.defaultBorderRadius};
    border-top-right-radius: ${(props) => props.theme.sizes.defaultBorderRadius};
  }

  div:last-child {
    border-bottom-left-radius: ${(props) =>
      props.theme.sizes.defaultBorderRadius};
    border-bottom-right-radius: ${(props) =>
      props.theme.sizes.defaultBorderRadius};
  }
`

export interface DropdownItemProps {
  label: string
  icon?: ArbeidsarkIconKeys
  onClick: () => void
}

const DropdownItem = styled.div`
  width: 194px;
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 14px 20px;
  color: ${(props) => props.theme.colors.gray50};
  svg > path {
    fill: ${(props) => props.theme.colors.gray50};
  }

  :hover {
    background: ${(props) => props.theme.colors.gray90};
    cursor: pointer;
  }
`

interface TableRowMenuProps {
  items: DropdownItemProps[]
  curtainOffset?: {
    x: number
    y: number
  }
}

export const TableRowMenu: React.FC<TableRowMenuProps> = ({
  items,
  curtainOffset,
}) => {
  const [show, setShow] = useState(false)
  const [growUp, setGrowUp] = useState(false)
  const dropdownCurtainRef = useRef(null)
  const rowMenuRef = useRef<HTMLDivElement | null>(null)
  const windowSize = useWindowSize()

  useClickOutside(rowMenuRef, (_e) => {
    setShow(false)
  })

  const dropdownItems = items.map((item, index) => (
    <DropdownItem onClick={item.onClick} key={index}>
      {item.icon && <SVGIcon icon={item.icon} />}
      {item.label}
    </DropdownItem>
  ))

  useEffect(() => {
    if (
      windowSize.height &&
      rowMenuRef.current?.getBoundingClientRect()?.y &&
      windowSize?.height - rowMenuRef.current?.getBoundingClientRect()?.y > 250
    ) {
      setGrowUp(false)
    } else {
      setGrowUp(true)
    }
  }, [windowSize])

  return (
    <MenuDropdownWrapper
      ref={rowMenuRef}
      onClick={() => {
        setShow(!show)
      }}
    >
      <SVGIcon icon="Ellipsis" />
      <MenuDropdownItemsWrapper
        show={show}
        growUp={growUp}
        offset={curtainOffset}
        ref={dropdownCurtainRef}
      >
        {dropdownItems}
      </MenuDropdownItemsWrapper>
    </MenuDropdownWrapper>
  )
}
